import { Component, OnInit, Input } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PortfolioMovementService, EnrollmentWithdrawalInfo, DeferEnrollmentRequest, ContinueFromDeferralRequest, PortfolioMovementResult } from '../../../services/portfolio-movement.service';
import { ParticipantService } from '../../../services/participant.service';
import { ProjectService } from '../../../services/project.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-deferral-wizard',
  templateUrl: './deferral-wizard.component.html',
  styleUrls: ['./deferral-wizard.component.scss']
})
export class DeferralWizardComponent implements OnInit {
  // 🔧 Exponer Math para uso en template
  Math = Math;

  @Input() wizardMode: 'new' | 'continue' = 'new';

  currentStep = 1;

  // Datos del wizard
  participants: any[] = [];
  selectedParticipantId: number | null = null;
  enrollments: EnrollmentWithdrawalInfo[] = [];
  deferredEnrollments: EnrollmentWithdrawalInfo[] = [];
  selectedEnrollment: EnrollmentWithdrawalInfo | null = null;
  selectedInstallments: number[] = [];
  projects: any[] = [];

  // Forms
  searchForm: FormGroup;
  destinationForm: FormGroup;
  confirmForm: FormGroup;

  // 🆕 Forms para nuevos pasos
  valueForm: FormGroup;

  // UI states
  isLoading = false;
  isSearching = false;
  isSubmitting = false;
  searchPerformed = false;

  // 🆕 Resultado de la operación (para paso final)
  operationResult: PortfolioMovementResult | null = null;
  operationSuccess = false;
  operationError = '';

  // Preselección
  preselectedEnrollmentId: number | null = null;

  // 🆕 Nuevas variables para los pasos extra
  newPaymentPlan: { installmentNumber: number; dueDate: Date; amount: number; }[] = [];

  // Variables para Modal de Refinanciación
  showRefinanceModal = false;
  refinanceInstallments = 2;
  minInstallments = 1;
  maxInstallments = 24;

  constructor(
    private fb: FormBuilder,
    private portfolioService: PortfolioMovementService,
    private participantService: ParticipantService,
    private projectService: ProjectService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    // ... (rest of constructor)
    this.searchForm = this.fb.group({
      searchTerm: ['', Validators.required]
    });

    this.destinationForm = this.fb.group({
      continueInSameProject: [true],
      newProjectId: [null]
    });

    this.valueForm = this.fb.group({
      newTotalValueCOP: [0, [Validators.min(0)]],
      newTotalValueUSD: [0, [Validators.min(0)]],
      currencyId: [1]
    });

    this.confirmForm = this.fb.group({
      reason: ['', [Validators.required, Validators.minLength(10)]],
      notes: ['']
    });
  }

  // ... (ngOnInit and other methods)

  // Métodos para Refinanciación Automática
  openRefinanceModal(): void {
    this.refinanceInstallments = Math.max(2, this.newPaymentPlan.length || 2);
    this.showRefinanceModal = true;
  }

  closeRefinanceModal(): void {
    this.showRefinanceModal = false;
  }

  confirmRefinance(): void {
    if (this.refinanceInstallments < this.minInstallments || this.refinanceInstallments > this.maxInstallments) {
      this.toastService.showToast('error', `El número de cuotas debe estar entre ${this.minInstallments} y ${this.maxInstallments}.`);
      return;
    }
    this.generatePaymentPlan(this.refinanceInstallments);
    this.closeRefinanceModal();
  }

  // Obtener el valor activo según la moneda seleccionada
  getActiveTotal(): number {
    const currencyId = this.valueForm.get('currencyId')?.value || 1;
    return currencyId === 1 
      ? this.parseAmount(this.valueForm.get('newTotalValueCOP')?.value)
      : this.parseAmount(this.valueForm.get('newTotalValueUSD')?.value);
  }

  // Autollenar desde el proyecto seleccionado
  autofillFromProject(): void {
    const project = this.getSelectedProject();
    if (project) {
      this.valueForm.patchValue({
        newTotalValueCOP: project.inscriptionAmountCOP || project.inscriptionAmount || 0,
        newTotalValueUSD: project.inscriptionAmountUSD || 0
      });
    }
  }

  generatePaymentPlan(installmentsCount: number): void {
    const totalValue = this.getActiveTotal();
    if (totalValue <= 0) {
      this.toastService.showToast('warning', 'El valor total debe ser mayor a 0 para generar un plan.');
      return;
    }

    this.newPaymentPlan = [];
    const baseAmount = totalValue / installmentsCount;
    // Redondear a 2 decimales
    const roundedAmount = Math.round((baseAmount + Number.EPSILON) * 100) / 100;

    let accumulated = 0;

    for (let i = 0; i < installmentsCount; i++) {
      const isLast = i === installmentsCount - 1;
      let amount = roundedAmount;

      if (isLast) {
        // Ajustar última cuota
        amount = Math.round((totalValue - accumulated + Number.EPSILON) * 100) / 100;
      } else {
        accumulated += roundedAmount;
      }

      this.newPaymentPlan.push({
        installmentNumber: i + 1,
        dueDate: this.calculateNextDueDate(i + 1),
        amount: amount
      });
    }
    this.toastService.showToast('success', `Plan generado exitosamente en ${installmentsCount} cuotas.`);
  }

  private calculateNextDueDate(installmentNumber: number): Date {
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(today.getMonth() + installmentNumber);

    // Ajuste de fin de mes
    if (nextMonth.getMonth() !== ((today.getMonth() + installmentNumber) % 12)) {
      nextMonth.setDate(0);
    }

    return nextMonth;
  }

  getEstimatedInstallmentAmount(): number {
    const total = this.getActiveTotal();
    if (this.refinanceInstallments <= 0) return 0;
    return total / this.refinanceInstallments;
  }

  ngOnInit(): void {
    this.loadProjects();

    this.route.queryParams.subscribe(params => {
      if (params['enrollmentId']) {
        this.preselectedEnrollmentId = +params['enrollmentId'];
        this.loadEnrollmentById(this.preselectedEnrollmentId);
      }
      if (params['mode'] === 'continue') {
        this.wizardMode = 'continue';
      }
    });
  }

  get totalSteps(): number {
    if (this.wizardMode === 'new') {
      return 4; // Seleccionar -> Cuotas -> Confirmar -> Resultado
    }
    // Modo continue
    const continueInSame = this.destinationForm.get('continueInSameProject')?.value;
    return continueInSame ? 4 : 6; // Mismo proyecto: 4 pasos, Nuevo proyecto: 6 pasos
  }

  get continueInSameProject(): boolean {
    return this.destinationForm.get('continueInSameProject')?.value === true;
  }

  isDestinationValid(): boolean {
    if (this.continueInSameProject) {
      return true; // No necesita proyecto si continúa en el mismo
    }
    return this.destinationForm.get('newProjectId')?.value != null;
  }

  get finalStepIndex(): number {
    return this.totalSteps;
  }

  // Helper para extraer datos de estructura paginada o array directo
  private extractData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  loadProjects(): void {
    this.projectService.getAll().subscribe({
      next: (response) => {
        this.projects = this.extractData(response);
      },
      error: () => {
        this.toastService.showToast('error', 'Error al cargar proyectos');
      }
    });
  }

  searchParticipant(): void {
    if (this.searchForm.invalid) return;

    this.isSearching = true;
    this.searchPerformed = true;
    const term = this.searchForm.get('searchTerm')?.value;

    this.participantService.getAllFiltered(term).subscribe({
      next: (response) => {
        this.participants = this.extractData(response);
        this.isSearching = false;
      },
      error: () => {
        this.toastService.showToast('error', 'Error al buscar participantes');
        this.isSearching = false;
      }
    });
  }

  selectParticipant(participant: any): void {
    this.selectedParticipantId = participant.id;
    if (this.wizardMode === 'new') {
      this.loadActiveEnrollments(participant.id);
    } else {
      this.loadDeferredEnrollments(participant.id);
    }
  }

  loadActiveEnrollments(participantId: number): void {
    this.isLoading = true;
    this.portfolioService.getActiveEnrollmentsByParticipant(participantId).subscribe({
      next: (response) => {
        this.enrollments = this.extractData(response);
        this.isLoading = false;
        if (this.enrollments.length === 0) {
          this.toastService.showToast('warning', 'Este participante no tiene inscripciones activas');
        }
      },
      error: () => {
        this.toastService.showToast('error', 'Error al cargar inscripciones');
        this.isLoading = false;
      }
    });
  }

  loadDeferredEnrollments(participantId: number): void {
    this.isLoading = true;
    this.portfolioService.getDeferredEnrollmentsByParticipant(participantId).subscribe({
      next: (response) => {
        this.deferredEnrollments = this.extractData(response);
        this.isLoading = false;
        if (this.deferredEnrollments.length === 0) {
          this.toastService.showToast('warning', 'Este participante no tiene inscripciones aplazadas');
        }
      },
      error: () => {
        this.toastService.showToast('error', 'Error al cargar inscripciones');
        this.isLoading = false;
      }
    });
  }

  loadEnrollmentById(enrollmentId: number): void {
    this.isLoading = true;
    this.portfolioService.getEnrollmentWithdrawalInfo(enrollmentId).subscribe({
      next: (response) => {
        const enrollment = response.data || response;
        if (enrollment) {
          this.selectedEnrollment = enrollment;
          if (enrollment.canBeDeferred) {
            this.wizardMode = 'new';
            this.selectedInstallments = enrollment.pendingPaymentPlans
              .filter((pp: any) => !pp.isPunished)
              .map((pp: any) => pp.paymentPlanId);
          } else if (enrollment.canContinueFromDeferral) {
            this.wizardMode = 'continue';
          } else {
            this.toastService.showToast('error', 'Esta inscripción no puede ser aplazada o continuada');
            this.router.navigate(['/enrollments']);
            return;
          }
          this.currentStep = 2;
        }
        this.isLoading = false;
      },
      error: () => {
        this.toastService.showToast('error', 'Error al cargar inscripción');
        this.isLoading = false;
      }
    });
  }

  selectEnrollment(enrollment: EnrollmentWithdrawalInfo): void {
    if (this.wizardMode === 'new' && !enrollment.canBeDeferred) {
      this.toastService.showToast('warning', 'Esta inscripción no tiene cuotas pendientes para aplazar');
      return;
    }
    if (this.wizardMode === 'continue' && !enrollment.canContinueFromDeferral) {
      this.toastService.showToast('warning', 'Esta inscripción no puede continuar');
      return;
    }
    this.selectedEnrollment = enrollment;
    if (this.wizardMode === 'new') {
      this.selectedInstallments = enrollment.pendingPaymentPlans
        .filter(pp => !pp.isPunished)
        .map(pp => pp.paymentPlanId);
    } else {
      // 🆕 Inicializar valor total con el valor de las cuotas aplazadas
      const deferredAmount = enrollment.pendingPaymentPlans
        .filter(pp => pp.isPunished)
        .reduce((sum, pp) => sum + pp.pendingAmount, 0);
      this.valueForm.patchValue({ newTotalValueCOP: deferredAmount, newTotalValueUSD: 0, currencyId: 1 });
    }
    this.currentStep = 2;
  }

  toggleInstallment(paymentPlanId: number): void {
    const index = this.selectedInstallments.indexOf(paymentPlanId);
    if (index > -1) {
      this.selectedInstallments.splice(index, 1);
    } else {
      this.selectedInstallments.push(paymentPlanId);
    }
  }

  selectAllInstallments(): void {
    if (!this.selectedEnrollment) return;
    this.selectedInstallments = this.selectedEnrollment.pendingPaymentPlans
      .filter(pp => !pp.isPunished)
      .map(pp => pp.paymentPlanId);
  }

  deselectAllInstallments(): void {
    this.selectedInstallments = [];
  }

  isInstallmentSelected(paymentPlanId: number): boolean {
    return this.selectedInstallments.includes(paymentPlanId);
  }

  get selectedTotalAmount(): number {
    if (!this.selectedEnrollment) return 0;
    return this.selectedEnrollment.pendingPaymentPlans
      .filter(pp => this.selectedInstallments.includes(pp.paymentPlanId))
      .reduce((sum, pp) => sum + pp.pendingAmount, 0);
  }

  get deferredInstallments() {
    return this.selectedEnrollment?.pendingPaymentPlans.filter(pp => pp.isPunished) || [];
  }

  get totalDeferredAmount(): number {
    return this.deferredInstallments.reduce((sum, pp) => sum + pp.pendingAmount, 0);
  }

  get allInstallmentsSelected(): boolean {
    if (!this.selectedEnrollment) return false;
    const availableInstallments = this.selectedEnrollment.pendingPaymentPlans.filter(pp => !pp.isPunished);
    return this.selectedInstallments.length === availableInstallments.length && availableInstallments.length > 0;
  }

  toggleSelectAll(): void {
    if (this.allInstallmentsSelected) {
      this.deselectAllInstallments();
    } else {
      this.selectAllInstallments();
    }
  }

  setMode(mode: 'new' | 'continue'): void {
    this.wizardMode = mode;
    this.selectedEnrollment = null;
    this.enrollments = [];
    this.deferredEnrollments = [];
    this.selectedInstallments = [];
    if (this.selectedParticipantId) {
      if (mode === 'new') {
        this.loadActiveEnrollments(this.selectedParticipantId);
      } else {
        this.loadDeferredEnrollments(this.selectedParticipantId);
      }
    }
  }

  // 🆕 Gestión del Plan de Pagos Nuevo
  initializeNewPaymentPlan(): void {
    if (this.newPaymentPlan.length === 0) {
      // Por defecto crear una cuota con el total
      const total = this.getActiveTotal();
      this.newPaymentPlan = [{
        installmentNumber: 1,
        dueDate: new Date(),
        amount: total
      }];
    }
  }

  addInstallment(): void {
    const nextNumber = this.newPaymentPlan.length + 1;
    // Vencimiento por defecto 30 días después de la anterior
    const lastDate = this.newPaymentPlan.length > 0
      ? new Date(this.newPaymentPlan[this.newPaymentPlan.length - 1].dueDate)
      : new Date();

    if (this.newPaymentPlan.length > 0) {
      lastDate.setMonth(lastDate.getMonth() + 1);
    }

    this.newPaymentPlan.push({
      installmentNumber: nextNumber,
      dueDate: lastDate,
      amount: 0
    });
  }

  removeInstallment(index: number): void {
    this.newPaymentPlan.splice(index, 1);
    // Renumerar
    this.newPaymentPlan.forEach((p, i) => p.installmentNumber = i + 1);
  }

  get currentPlanTotal(): number {
    return this.newPaymentPlan.reduce((sum, p) => sum + (p.amount || 0), 0);
  }

  get isPlanValid(): boolean {
    const targetTotal = this.getActiveTotal();
    // Permitir una pequeña diferencia por decimales
    return Math.abs(this.currentPlanTotal - targetTotal) < 0.1 && this.newPaymentPlan.length > 0;
  }

  nextStep(): void {
    if (this.currentStep === 1 && this.selectedEnrollment) {
      this.currentStep = 2;
    } else if (this.currentStep === 2) {
      if (this.wizardMode === 'new' && this.selectedInstallments.length > 0) {
        this.currentStep = 3;
      } else if (this.wizardMode === 'continue' && this.isDestinationValid()) {
        if (this.continueInSameProject) {
          // Saltar a Confirmar directamente (paso 3 en flujo de 4 pasos)
          this.currentStep = 3;
        } else {
          // Ir a Modificar Valor (paso 3 en flujo de 6 pasos)
          this.currentStep = 3;
        }
      }
    } else if (this.currentStep === 3) {
      if (this.wizardMode === 'continue') {
        if (this.continueInSameProject) {
          // Ya está en Confirmar, no avanza más hasta submit
        } else if (this.valueForm.valid) {
          this.initializeNewPaymentPlan();
          this.currentStep = 4; // Ir a Plan de Pagos
        }
      }
    } else if (this.currentStep === 4) {
      if (this.wizardMode === 'continue' && !this.continueInSameProject && this.isPlanValid) {
        this.currentStep = 5; // Ir a Confirmar
      }
    }
  }

  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  submitDeferral(): void {
    if (this.confirmForm.invalid || !this.selectedEnrollment || this.isSubmitting) return;

    this.isSubmitting = true;

    if (this.wizardMode === 'new') {
      const request: DeferEnrollmentRequest = {
        enrollmentId: this.selectedEnrollment.enrollmentId,
        reason: this.confirmForm.get('reason')?.value,
        paymentPlanIds: this.selectedInstallments,
        notes: this.confirmForm.get('notes')?.value
      };

      this.portfolioService.deferEnrollment(request).subscribe({
        next: (response) => {
          this.handleResponse(response);
        },
        error: () => {
          this.handleError();
        }
      });
    } else {
      // Modo continue (desde aplazamiento)
      const continueInSame = this.continueInSameProject;

      let request: ContinueFromDeferralRequest;

      if (continueInSame) {
        // Reversión de aplazamiento - continuar en el mismo proyecto
        request = {
          enrollmentId: this.selectedEnrollment.enrollmentId,
          continueInSameProject: true,
          reason: this.confirmForm.get('reason')?.value,
          notes: this.confirmForm.get('notes')?.value
        };
      } else {
        // Continuar en nuevo proyecto - lógica original
        const cleanedPaymentPlan = this.newPaymentPlan.map(installment => ({
          installmentNumber: installment.installmentNumber,
          dueDate: installment.dueDate,
          amount: this.parseAmount(installment.amount)
        }));

        request = {
          enrollmentId: this.selectedEnrollment.enrollmentId,
          continueInSameProject: false,
          newProjectId: this.destinationForm.get('newProjectId')?.value,
          reason: this.confirmForm.get('reason')?.value,
          notes: this.confirmForm.get('notes')?.value,
          newTotalValue: this.getActiveTotal(),
          newPaymentPlan: cleanedPaymentPlan
        };
      }

      console.log('📤 Enviando solicitud de continuación:', request);

      this.portfolioService.continueFromDeferral(request).subscribe({
        next: (response) => {
          this.handleResponse(response);
        },
        error: () => {
          this.handleError();
        }
      });
    }
  }

  // Método para parsear montos que pueden venir como string formateado o número
  private parseAmount(value: any): number {
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string') {
      // Remover formato de moneda: $ 1.234.567,89 -> 1234567.89
      const cleaned = value
        .replace(/[$\s]/g, '')  // Remover $ y espacios
        .replace(/\./g, '')     // Remover puntos de miles
        .replace(/,/g, '.');    // Convertir coma decimal a punto
      return parseFloat(cleaned) || 0;
    }
    return 0;
  }

  private handleResponse(response: any): void {
    const result = response.data || response;
    this.operationResult = result;
    this.isSubmitting = false;

    if (result.success) {
      this.operationSuccess = true;
      this.currentStep = this.finalStepIndex;
    } else {
      this.operationSuccess = false;
      this.operationError = result.message || 'Error al procesar operación';
      this.currentStep = this.finalStepIndex;
    }
  }

  private handleError(): void {
    this.operationSuccess = false;
    this.operationError = 'Error al procesar operación. Por favor, intente nuevamente.';
    this.currentStep = this.finalStepIndex;
    this.isSubmitting = false;
  }

  cancel(): void {
    this.router.navigate(['/enrollments']);
  }

  getSelectedProject(): any {
    const projectId = this.destinationForm.get('newProjectId')?.value;
    return this.projects.find(p => p.id === projectId);
  }

  // 🆕 Métodos para el paso final
  viewEnrollment(): void {
    const targetEnrollment = this.operationResult?.newEnrollmentId || this.selectedEnrollment?.enrollmentId;
    if (targetEnrollment) {
      this.router.navigate(['/enrollments/view', targetEnrollment]);
    }
  }

  viewOriginalEnrollment(): void {
    if (this.selectedEnrollment) {
      this.router.navigate(['/enrollments/view', this.selectedEnrollment.enrollmentId]);
    }
  }

  goToEnrollmentsList(): void {
    this.router.navigate(['/enrollments']);
  }

  startNewDeferral(): void {
    this.currentStep = 1;
    this.wizardMode = 'new';
    this.selectedParticipantId = null;
    this.selectedEnrollment = null;
    this.selectedInstallments = [];
    this.enrollments = [];
    this.deferredEnrollments = [];
    this.participants = [];
    this.searchPerformed = false;
    this.operationResult = null;
    this.operationSuccess = false;
    this.operationError = '';
    this.searchForm.reset();
    this.destinationForm.reset();
    this.confirmForm.reset();
    this.valueForm.reset({ newTotalValueCOP: 0, newTotalValueUSD: 0, currencyId: 1 });
    this.newPaymentPlan = [];
  }
}
